import React, { useState, useMemo, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FaBell, FaFilter } from 'react-icons/fa';
import { useNotifications } from '../../context/NotificationContext';
import { Notification } from '../../types/notification';
import NotificationItem from './NotificationItem';
import { eventsService } from '../../services/events.service';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useAuth } from '../../context/AuthContext';
import { useUserFilter } from '../../context/UserFilterContext';
import { useAppContext } from '../../context/AppContext';

const NotificationContainer = styled.div`
  position: relative; /* Changer de fixed à relative pour s'intégrer dans le conteneur parent */
  z-index: 1000;
`;

const BellContainer = styled.div`
  position: relative;
  cursor: pointer;
  width: 40px;
  height: 40px;
  background-color: #ffd700;
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
  transition: background-color 0.2s, transform 0.2s;
  
  /* Réduire uniquement sur mobile */
  @media (max-width: 768px) {
    width: 30px;
    height: 30px;
  }

  &:hover {
    background-color: #ffed4a;
    transform: scale(1.05);
  }

  svg {
    font-size: 18px;
    color: #000;
  }
`;

const NotificationCount = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  border: 2px solid #fff;
`;

const NotificationPanel = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 60px); /* Augmenté pour passer sous la barre de recherche */
  right: 0;
  width: 675px;
  max-height: 500px;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  display: ${({ $isOpen }) => $isOpen ? 'block' : 'none'};
  padding: 10px;
  z-index: 1002; /* S'assurer que le panel est au-dessus des autres éléments */

  @media (max-width: 1024px) {
    width: 550px;
    right: 0;
    transform: none;
    top: calc(100% + 60px); /* Maintenir le même espacement */
  }

  @media (max-width: 768px) {
    position: fixed;
    top: 120px; /* Position fixe sous la barre de recherche en mobile */
    right: 10px;
    width: 90vw;
    max-width: 400px;
    max-height: 80vh; /* Limiter la hauteur pour éviter de déborder de l'écran */
    transform: none;
  }
  
  @media (max-width: 480px) {
    width: 95vw;
    max-width: 350px;
    right: 10px;
    top: 120px; /* Maintenir la position en mobile */
  }
`;

const TabContainer = styled.div`
  display: flex;
  margin-bottom: 10px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Tab = styled.div<{ $active: boolean }>`
  padding: 8px 16px;
  cursor: pointer;
  font-weight: ${({ $active }) => $active ? 'bold' : 'normal'};
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.text};
  border-bottom: 2px solid ${({ $active, theme }) => $active ? theme.colors.primary : 'transparent'};
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  span.counter {
    display: inline-block;
    margin-left: 4px;
  }
`;

const FilterButton = styled.button<{ $active: boolean }>`
  background: ${({ $active, theme }) => $active ? theme.colors.primary : 'transparent'};
  color: ${({ $active }) => $active ? '#fff' : 'inherit'};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  transition: all 0.2s;
  
  &:hover {
    background: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.border};
  }
  
  svg {
    font-size: 12px;
  }
`;

const EmptyNotification = styled.div`
  padding: 20px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textLight};
`;

const _ClearAllButton = styled.button`
  padding: 8px;
  margin-left: auto;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textLight};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const _NOTIFICATION_WINDOW = 24 * 60 * 60 * 1000; // Fenêtre de notification de 24 heures

export const NotificationBell: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'unread' | 'history' | 'upcoming'>('unread');
  const { state: { notifications }, clearAll, markAsRead } = useNotifications();
  const { getUserInfo: _getUserInfo } = useUserProfile();
  const { userProfile } = useAuth();
  const { selectedUserId } = useUserFilter();
  const { state: appState, dispatch } = useAppContext();
  
  // Vérifier si l'utilisateur est admin et si le mode "Tous les utilisateurs" est activé
  const isAdmin = userProfile?.is_owner || false;
  const showUserAvatars = isAdmin && selectedUserId === null; // null signifie "Tous les utilisateurs"
  
  // Handler pour le bouton filtre
  const handleToggleFilter = useCallback((e: React.MouseEvent) => {
    e.preventDefault(); // Empêcher le comportement par défaut
    e.stopPropagation(); // Empêcher la propagation au Tab
    dispatch({ type: 'TOGGLE_SHOW_ONLY_MARKERS_WITH_NOTIFICATIONS', payload: null });
  }, [dispatch]);

  // Ajout d'un état pour forcer la mise à jour
  const [updateTrigger, setUpdateTrigger] = useState(0);

  // Force une mise à jour toutes les minutes
  useEffect(() => {
    const interval = setInterval(() => {
      setUpdateTrigger(prev => prev + 1);
    }, 60000); // 1 minute

    return () => clearInterval(interval);
  }, []);

  // Filtrer et trier les notifications
  const { unreadNotifications, historyNotifications, upcomingReminders } = useMemo(() => {
    const now = new Date();
    
    // Filtrer les notifications qui sont des changements de catégorie ou des annulations de suppression
    const filteredNotifications = notifications.filter(notification => {
      // Filtrer les changements de catégorie
      const isCategoryChange = notification.type === 'info' && 
        notification.message && 
        notification.message.includes('a changé de catégorie');
      
      // Filtrer les annulations de suppression
      const isCancelledDeletion = notification.type === 'info' && 
        notification.title === 'Suppression annulée';
      
      // Exclure les deux types de notifications
      return !isCategoryChange && !isCancelledDeletion;
    });

    // Forcer une vérification du statut des notifications
    const unread: Notification[] = [];
    const history: Notification[] = [];
    const upcoming: Notification[] = [];

    // Traiter chaque notification une seule fois et la placer dans la bonne catégorie
    filteredNotifications.forEach(notification => {
      // S'assurer que les dates sont des objets Date
      const _createdAt = notification.createdAt instanceof Date 
        ? notification.createdAt 
        : new Date(notification.createdAt);
      
      // Traiter les rappels à venir
      if (notification.type === 'reminder' && notification.reminderDate) {
        const reminderDate = notification.reminderDate instanceof Date 
          ? notification.reminderDate 
          : new Date(notification.reminderDate);
        
        // Si la notification est lue, toujours l'ajouter à l'historique
        if (notification.status === 'read') {
          history.push(notification);
          return;
        }
        
        // Si la date de rappel est dans le futur et que la notification n'est pas lue, c'est un rappel à venir
        if (reminderDate > now && (!notification.status || notification.status === 'unread')) {
          upcoming.push(notification);
          return; // Ne pas traiter cette notification plus loin
        }
        
      }
      
      // Pour les autres notifications (non-upcoming)
      if (!notification.status || notification.status === 'unread') {
        unread.push(notification);
      } else if (notification.status === 'read') {
        history.push(notification);
      }
    });

    // Trier les notifications
    const sortedUnread = unread.sort((a, b) => {
      const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
      const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
      return dateB.getTime() - dateA.getTime();
    });
    
    const sortedHistory = history.sort((a, b) => {
      const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
      const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
      return dateB.getTime() - dateA.getTime();
    });
    
    const sortedUpcoming = upcoming.sort((a, b) => {
      // Gérer de façon sécurisée les cas où reminderDate pourrait être undefined ou null
      const getValidDate = (notification: any): Date => {
        if (!notification.reminderDate) {
          // Si pas de date de rappel, utiliser une date très future pour placer à la fin du tri
          // ou une date passée selon la logique métier souhaitée
          return new Date(Date.now() + 1000 * 60 * 60 * 24 * 365); // Date dans un an
        }
        return notification.reminderDate instanceof Date 
          ? notification.reminderDate 
          : new Date(notification.reminderDate);
      };
      
      const dateA = getValidDate(a);
      const dateB = getValidDate(b);
      return dateA.getTime() - dateB.getTime();
    });

    return {
      unreadNotifications: sortedUnread,
      historyNotifications: sortedHistory,
      upcomingReminders: sortedUpcoming
    };
  }, [notifications, updateTrigger]); // Ajout de updateTrigger comme dépendance

  const handleNotificationClick = useCallback((notification: Notification) => {
    // Si la notification est liée à un marker, ouvrir le modal du marker
    if (notification.markerId) {
      // Pour les notifications de type 'reminder', ne pas marquer comme lue automatiquement
      // La notification sera marquée comme lue uniquement quand l'utilisateur 
      // clique manuellement sur le check dans la modale
      
      // Émettre un événement pour ouvrir le modal du marker avec les données de notification
      eventsService.emit('OPEN_MARKER_MODAL', { 
        markerId: notification.markerId,
        notificationId: notification.id,
        notificationTitle: notification.title,
        actionId: notification.actionId // Ajouter l'ID de l'action pour la mise en évidence
      });
      
      // Fermer le panneau de notifications
      setIsOpen(false);
      return;
    }

    // Pour toutes les autres notifications (info, warning, etc.) sans markerId
    // Marquer comme lue immédiatement quand on clique dessus
    markAsRead(notification.id);
    // Fermer le panneau de notifications
    setIsOpen(false);
  }, [setIsOpen, markAsRead]);

  const _handleClearAll = useCallback(() => {
    clearAll();
  }, [clearAll]);

  // Forcer les compteurs à être des nombres
  const unreadCount = unreadNotifications ? unreadNotifications.length : 0;
  const upcomingCount = upcomingReminders ? upcomingReminders.length : 0;
  const historyCount = historyNotifications ? historyNotifications.length : 0;
  
  // Force le rendu des compteurs
  const unreadText = `Non lues (${unreadCount})`;
  const upcomingText = `Rappels à venir (${upcomingCount})`;
  const historyText = `Historique (${historyCount})`;

  return (
    <NotificationContainer>
      <BellContainer onClick={() => setIsOpen(!isOpen)}>
        <FaBell />
        {unreadCount > 0 && <NotificationCount>{unreadCount}</NotificationCount>}
      </BellContainer>

      <NotificationPanel $isOpen={isOpen}>
        <TabContainer>
          <Tab 
            $active={activeTab === 'unread'} 
            onClick={() => setActiveTab('unread')}
          >
            {unreadText}
            {activeTab === 'unread' && (
              <FilterButton 
                $active={appState.showOnlyMarkersWithNotifications}
                onClick={handleToggleFilter}
                title={appState.showOnlyMarkersWithNotifications ? "Afficher tous les marqueurs" : "Afficher uniquement les marqueurs avec notifications"}
              >
                <FaFilter />
              </FilterButton>
            )}
          </Tab>
          <Tab 
            $active={activeTab === 'upcoming'} 
            onClick={() => setActiveTab('upcoming')}
          >
            {upcomingText}
          </Tab>
          <Tab 
            $active={activeTab === 'history'} 
            onClick={() => setActiveTab('history')}
          >
            {historyText}
          </Tab>
        </TabContainer>

        {activeTab === 'unread' ? (
          unreadCount === 0 ? (
            <EmptyNotification>Aucune notification non lue</EmptyNotification>
          ) : (
            unreadNotifications.map(notification => (
              <NotificationItem 
                key={notification.id}
                notification={notification}
                isRead={false}
                showUserAvatar={showUserAvatars}
                onClick={() => handleNotificationClick(notification)}
              />
            ))
          )
        ) : activeTab === 'history' ? (
          historyCount === 0 ? (
            <EmptyNotification>Aucune notification dans l'historique</EmptyNotification>
          ) : (
            historyNotifications.map(notification => (
              <NotificationItem 
                key={notification.id}
                notification={notification}
                isRead={true}
                showUserAvatar={showUserAvatars}
                isHistory={true}
              />
            ))
          )
        ) : (
          upcomingCount === 0 ? (
            <EmptyNotification>Aucun rappel à venir</EmptyNotification>
          ) : (
            upcomingReminders.map(notification => (
              <NotificationItem 
                key={notification.id}
                notification={notification}
                isRead={notification.status === 'read'}
                showUserAvatar={showUserAvatars}
                onClick={() => handleNotificationClick(notification)}
              />
            ))
          )
        )}
      </NotificationPanel>
    </NotificationContainer>
  );
};

